import React, { useState, useRef, useEffect, useMemo } from "react";
import { connect, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
    approveRequest,
    rejectRequest,
    setRequest,
    getAssigneeEmployee,
    assignSupervisor,
    getActivators,
    assignStaffs,
    deactivateRequest
} from "../../store/actions";
import { Button, GlassModal, useToast } from "../UI";
import { Edit2, User, CreditCard, CheckCircle2, PlusCircle, MinusCircle, ShieldOff, Clock3, XCircle, ExternalLink, Paperclip } from "lucide-react";
import moment from "moment";
import axios from "axios";
import { axiosConfig } from "../../config/index";

interface RequestDetailsProps {
    isOpen: boolean;
    onClose: () => void;
    onRefresh: () => void;
    singleRequst: any;
    user: any;
    approveRequest: (id: string, staffId?: string, physicalCardId?: string) => Promise<any>;
    rejectRequest: (id: string, reason: string) => Promise<any>;
    setRequest: (req: any) => void;
    getAssigneeEmployee: () => void;
    assignSupervisor: (id: string, staffId: string) => Promise<any>;
    getActivators: () => void;
    assignStaffs: (id: string, staffIds: string[]) => Promise<any>;
    deactivateRequest: (id: string) => Promise<any>;
}

const RequestDetails: React.FC<RequestDetailsProps> = ({
    isOpen,
    onClose,
    onRefresh,
    singleRequst,
    user,
    approveRequest,
    rejectRequest,
    setRequest,
    getAssigneeEmployee,
    assignSupervisor,
    getActivators,
    assignStaffs,
    deactivateRequest }) => {
    const { toast } = useToast();
    const [rejectionMode, setRejectionMode] = useState(false);
    const [rejectionReason, setRejectionReason] = useState("");
    const [showAssignmentSection, setShowAssignmentSection] = useState(false);
    const [selectedStaffs, setSelectedStaffs] = useState<string[]>([]);
    const [physicalCardId, setPhysicalCardId] = useState<string>("");
    const [activeCard, setActiveCard] = useState<any>(null);
    const [showConfirmDeactivation, setShowConfirmDeactivation] = useState(false);
    const [showConfirmActivation, setShowConfirmActivation] = useState(false);
    const [showFooter, setShowFooter] = useState(false);

    const assignmentRef = useRef<HTMLDivElement>(null);
    const rejectionRef = useRef<HTMLTextAreaElement>(null);
    const footerRef = useRef<HTMLDivElement>(null);
    const cardInputRef = useRef<HTMLInputElement>(null);
    const deactivationRef = useRef<HTMLDivElement>(null);
    const modalBodyRef = useRef<HTMLDivElement>(null);

    const emp = useSelector((state: any) => state.request.assigneeEmployee);
    const filteredEmp = useMemo(() => {
        if (!emp) return [];
        if (singleRequst?.approvalStage === "Electronic Security Manager") {
            return emp.filter((staff: any) =>
                (staff.role?.name?.toLowerCase() === 'supervisor') ||
                (staff.role?.name?.toLowerCase() === 'electronic security specialist') ||
                (staff.roles && staff.roles.some((r: any) =>
                    r.name?.toLowerCase() === 'supervisor' ||
                    r.name?.toLowerCase() === 'electronic security specialist'
                )) ||
                (staff.customRoles && staff.customRoles.some((r: string) =>
                    r.toLowerCase() === 'supervisor' ||
                    r.toLowerCase() === 'electronic security specialist'
                )) ||
                (staff.jobRole?.toLowerCase() === 'supervisor') ||
                (staff.jobRole?.toLowerCase() === 'electronic security specialist')
            );
        }
        if (singleRequst?.approvalStage === "Security and Surveillance Supervisor" || singleRequst?.approvalStage === "Card Activator") {
            return emp.filter((staff: any) =>
                (staff.role?.name?.toLowerCase() === 'card activator') ||
                (staff.jobTitle?.toLowerCase() === 'card activator') ||
                (staff.jobRole?.toLowerCase() === 'card activator') ||
                (staff.customRoles && staff.customRoles.some((r: string) => r.toLowerCase() === 'card activator')) ||
                (staff.roles && staff.roles.some((r: any) => r.name?.toLowerCase() === 'card activator'))
            );
        }
        return emp;
    }, [emp, singleRequst?.approvalStage]);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const scrollToField = (element: HTMLElement | null, block: ScrollLogicalPosition = 'center') => {
        if (!element) return;
        element.scrollIntoView({ behavior: 'smooth', block });
    };

    useEffect(() => {
        if (isOpen) {
            if (singleRequst?.approvalStage === "Security and Surveillance Supervisor" || singleRequst?.approvalStage === "Card Activator") {
                getActivators();
            } else {
                getAssigneeEmployee();
            }
        } else {
            setRejectionMode(false);
            setRejectionReason("");
            setShowAssignmentSection(false);
            setSelectedStaffs([]);
            setPhysicalCardId("");
            setActiveCard(null);
            setShowConfirmActivation(false);
            setShowConfirmDeactivation(false);
            setShowFooter(false);
            setIsSubmitting(false);
        }
    }, [isOpen, singleRequst?.approvalStage, getAssigneeEmployee, getActivators]);

    // Fetch active card when the Card Activator opens the request
    useEffect(() => {
        const requesterOracleId = singleRequst?.requester?.oracleId || singleRequst?.requesterId;
        if (isOpen && singleRequst?.approvalStage === "Card Activator" && requesterOracleId) {
            axios.get(`/api/cards/employee/${requesterOracleId}`, axiosConfig)
                .then(res => setActiveCard(res.data))
                .catch(() => setActiveCard(null));
        }
    }, [isOpen, singleRequst?.approvalStage, singleRequst?.requester?.oracleId, singleRequst?.requesterId]);



    const isRejected = singleRequst?.status === 'Rejected';
    const isRequester = user?._id == (singleRequst?.requester?._id || singleRequst?.requesterId);
    const userRoles = user?.roles || (user?.role ? [user.role] : []);
    const hasRole = (role: string) =>
        user?.jobRole?.toLowerCase() === role.toLowerCase() ||
        userRoles.some((r: any) => r.name?.toLowerCase() === role.toLowerCase()) ||
        (Array.isArray(user?.customRoles) && user?.customRoles.some((r: string) => r.toLowerCase() === role.toLowerCase()));
    const isCardActivator = hasRole('card activator') ||
        userRoles.some((r: any) =>
            r.permissions?.activate_cards === "FULL" ||
            (Array.isArray(r.permissions) && r.permissions.includes('activate:requests'))
        );
    const isESM = hasRole('Electronic Security Manager');
    const isSupervisor = hasRole('Security and Surveillance Supervisor') || hasRole('Supervisor');

    const isAssignedApprover =
        singleRequst?.approver?._id == user?._id ||
        singleRequst?.approverId == user?._id ||
        singleRequst?.approverId == user?.oracleId ||
        singleRequst?.approverId == user?.employeeId;

    // A user is authorized to act on a request if:
    // - The request is at their role's stage AND (they are assigned OR it is in their pool)
    const isAuthorized =
        (singleRequst?.approvalStage === "Security Manager" && singleRequst?.lockedById == user?._id) ||
        (singleRequst?.approvalStage === "Electronic Security Manager" && (isAssignedApprover || isESM)) ||
        (singleRequst?.approvalStage === "Security and Surveillance Supervisor" && (isAssignedApprover || !singleRequst?.approverId) && (isSupervisor || hasRole('Electronic Security Specialist'))) ||
        (singleRequst?.approvalStage === "Card Activator" && (isAssignedApprover || !singleRequst?.approverId) && isCardActivator) ||
        (isAssignedApprover && (singleRequst?.approvalStage === "Manager" || singleRequst?.approvalStage === "Officer" || singleRequst?.approvalStage === "Executive" || singleRequst?.approvalStage === "Excutive"));
    const isObserver = singleRequst?.approvalStage === "Security Manager" && singleRequst?.lockedById && singleRequst?.lockedById != user?._id;

    if (!singleRequst) return null;

    const handleApprove = async () => {
        if (isSubmitting) return;
        const reqType = singleRequst?.requesttype;
        const stage = singleRequst?.approvalStage;
        // Card ID is required only for 'new' and 'replacement' at Card Activator stage
        if (stage === "Card Activator" && (reqType === "new" || reqType === "replacement")) {
            if (!physicalCardId.trim()) {
                toast("Please scan or enter the physical card ID before confirming activation.", "warning");
                return;
            }
        }
        setIsSubmitting(true);
        try {
            await approveRequest(singleRequst._id, undefined, physicalCardId || undefined);
            if (stage === "Card Activator") {
                const actionWord = reqType === "deactivation" ? "Deactivated" : "Activated";
                toast(`Card ${actionWord} Successfully`, 'success');
            } else {
                toast('Request Approved Successfully', 'success');
            }
            onClose();
            onRefresh();
        } catch (err: any) {
            const errorMsg = err?.message || err?.approver || err?.request || (typeof err === "string" ? err : JSON.stringify(err)) || "Approval failed. Please try again.";
            toast(errorMsg, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReject = async () => {
        if (isSubmitting) return;
        if (!rejectionReason) {
            toast("Please provide a reason for rejection", 'warning');
            return;
        }
        setIsSubmitting(true);
        try {
            await rejectRequest(singleRequst._id, rejectionReason);
            // toast("Request rejected successfully", 'success'); // suppressed per UI spec
            setRejectionMode(false);
            setRejectionReason("");
            onClose();
            onRefresh();
        } catch (err: any) {
            const errorMsg = err?.message || err?.request || "Rejection failed";
            toast(errorMsg, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAssignAction = async () => {
        if (isSubmitting) return;
        if (selectedStaffs.length === 0) {
            toast(singleRequst?.approvalStage === "Electronic Security Manager" ? "Please select a supervisor" : "Please select an activator", "warning");
            return;
        }

        setIsSubmitting(true);
        try {
            if (singleRequst?.approvalStage === "Electronic Security Manager") {
                await assignSupervisor(singleRequst._id, selectedStaffs[0]);
                toast("Supervisor assigned successfully", "success");
            } else if (singleRequst?.approvalStage === "Security and Surveillance Supervisor") {
                await approveRequest(singleRequst._id, selectedStaffs[0]);
                toast("Activator assigned successfully", "success");
            } else {
                await assignStaffs(singleRequst._id, selectedStaffs);
                toast("Staff assigned successfully", 'success');
            }
            onClose();
            setShowAssignmentSection(false);
            onRefresh();
        } catch (err: any) {
            const errorMsg = err?.message || "Assignment failed";
            toast(errorMsg, "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <GlassModal
            isOpen={isOpen}
            onClose={onClose}
            title="Request Details"
            maxWidth="max-w-2xl"
            bodyRef={modalBodyRef}
            footer={
                <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 w-full">
                    <Button
                        variant="ghost"
                        onClick={onClose}
                        className="w-full sm:w-auto text-slate-600 hover:text-slate-700 font-medium text-sm transition-colors border border-orange-400/50 hover:border-orange-500/70 rounded-xl px-5 py-2 hover:bg-orange-100/50"
                    >
                        Cancel
                    </Button>

                    <div className="flex flex-col-reverse sm:flex-row items-center gap-3 w-full sm:w-auto">
                        {isRejected && isRequester && (
                            <Link
                                to="/editrequest"
                                onClick={() => { setRequest(singleRequst); onClose(); }}
                                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white text-sm font-medium rounded-xl transition-colors shadow-xs"
                            >
                                <Edit2 className="w-4 h-4" />
                                Edit & Resubmit
                            </Link>
                        )}

                        {isAuthorized && (
                            <div className="flex flex-col-reverse sm:flex-row items-center gap-3 w-full sm:w-auto">
                                {rejectionMode ? (
                                    <>
                                        <Button
                                            variant="ghost"
                                            className="w-full sm:w-auto border border-slate-300 hover:border hover:border-slate-400   text-slate-500 hover:bg-slate-50 rounded-xl font-medium text-sm px-4 py-2 transition-all"
                                            onClick={() => {
                                                setRejectionMode(false);
                                            }}
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            className="w-full sm:w-auto border border-rose-300 !bg-rose-500/90 hover:!bg-rose-600 !text-white rounded-xl font-medium text-sm px-4 py-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                            disabled={isSubmitting}
                                            onClick={handleReject}
                                        >
                                            {isSubmitting ? "Rejecting…" : "Confirm Rejection"}
                                        </Button>

                                    </>
                                ) : (
                                    <>
                                        <Button
                                            variant="ghost"
                                            className="w-full sm:w-auto !border border-rose-400 text-slate-600 hover:text-slate-800 hover:!bg-rose-100 rounded-xl font-medium text-sm px-4 py-2"
                                            onClick={() => {
                                                setRejectionMode(true);
                                                setShowAssignmentSection(false);
                                                rejectionRef.current?.focus({ preventScroll: true });
                                                setTimeout(() => {
                                                    scrollToField(rejectionRef.current, 'center');
                                                }, 300);
                                            }}
                                        >
                                            Reject
                                        </Button>

                                        {(() => {
                                            const stage = singleRequst?.approvalStage;
                                            const reqType = singleRequst?.requesttype;

                                            // ESM: show Assign Supervisor button
                                            if (stage === "Electronic Security Manager" && isESM) {
                                                if (!showAssignmentSection) {
                                                    return (
                                                        <Button
                                                            className="w-full sm:w-auto bg-[#8dc63f] hover:bg-[#7ba337] text-white font-medium rounded-xl px-6 py-2 text-sm shadow-xs transition-all active:scale-95"
                                                            onClick={() => {
                                                                setShowAssignmentSection(true);
                                                                setSelectedStaffs([]);
                                                                setTimeout(() => {
                                                                    scrollToField(assignmentRef.current, 'start');
                                                                }, 400);
                                                            }}
                                                        >
                                                            Assign Supervisor
                                                        </Button>
                                                    );
                                                }
                                                return (
                                                    <Button
                                                        className="w-full sm:w-auto bg-[#8dc63f] hover:bg-[#7ba337] text-white font-medium rounded-xl px-6 py-2 text-sm shadow-xs transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        disabled={isSubmitting}
                                                        onClick={handleAssignAction}
                                                    >
                                                        {isSubmitting ? "Assigning…" : "Confirm Assignment"}
                                                    </Button>
                                                );
                                            }

                                            // Supervisor or Specialist: show Assign Activator button
                                            if (stage === "Security and Surveillance Supervisor" && (isSupervisor || hasRole('Electronic Security Specialist') || isAssignedApprover)) {
                                                if (!showAssignmentSection) {
                                                    return (
                                                        <Button
                                                            className="w-full sm:w-auto bg-[#8dc63f] hover:bg-[#7ba337] text-white font-medium rounded-xl px-6 py-2 text-sm shadow-xs transition-all active:scale-95"
                                                            onClick={() => {
                                                                setShowAssignmentSection(true);
                                                                setSelectedStaffs([]);
                                                                setTimeout(() => {
                                                                    scrollToField(assignmentRef.current, 'start');
                                                                }, 400);
                                                            }}
                                                        >
                                                            Assign Activator
                                                        </Button>
                                                    );
                                                }
                                                return (
                                                    <Button
                                                        className="w-full sm:w-auto bg-[#8dc63f] hover:bg-[#7ba337] text-white font-medium rounded-xl px-6 py-2 text-sm shadow-xs transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        disabled={isSubmitting}
                                                        onClick={handleAssignAction}
                                                    >
                                                        {isSubmitting ? "Assigning…" : "Confirm Assignment"}
                                                    </Button>
                                                );
                                            }

                                            // Card Activator: show Activate/Deactivate button
                                            if (stage === "Card Activator" && isCardActivator) {
                                                if (!showConfirmActivation && !showConfirmDeactivation) {
                                                    return (
                                                        <Button
                                                            className="w-full sm:w-auto bg-[#8dc63f] hover:bg-[#7ba337] text-white font-medium rounded-xl px-6 py-2 text-sm shadow-xs transition-all active:scale-95"
                                                            onClick={() => {
                                                                if (reqType === "deactivation") {
                                                                    setShowConfirmDeactivation(true);
                                                                    setTimeout(() => {
                                                                        scrollToField(deactivationRef.current, 'nearest');
                                                                    }, 300);
                                                                } else if (reqType === "maintenance") {
                                                                    setShowConfirmActivation(true);
                                                                    setTimeout(() => {
                                                                        scrollToField(deactivationRef.current, 'nearest');
                                                                    }, 300);
                                                                } else {
                                                                    setShowConfirmActivation(true);
                                                                    cardInputRef.current?.focus({ preventScroll: true });
                                                                    setTimeout(() => {
                                                                        scrollToField(cardInputRef.current, 'center');
                                                                    }, 300);
                                                                }
                                                            }}
                                                        >
                                                            {reqType === "deactivation" ? "Deactivate" : "Activate"}
                                                        </Button>
                                                    );
                                                }
                                                return (
                                                    <Button
                                                        className="w-full sm:w-auto bg-[#8dc63f] hover:bg-[#7ba337] text-white font-medium rounded-xl px-6 py-2 text-sm shadow-xs transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        disabled={isSubmitting}
                                                        onClick={handleApprove}
                                                    >
                                                        {isSubmitting ? "Processing…" : `Confirm ${reqType === "deactivation" ? "Deactivation" : "Activation"}`}
                                                    </Button>
                                                );
                                            }

                                            // Manager / Officer / Executive: standard Approve
                                            if (stage === "Manager" || stage === "Officer" || stage === "Executive" || stage === "Excutive" || stage === "Security Manager") {
                                                return (
                                                    <Button
                                                        className="w-full sm:w-auto bg-[#8dc63f] hover:bg-[#7ba337] text-white font-medium rounded-xl px-6 py-2 text-sm shadow-xs transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        disabled={isSubmitting}
                                                        onClick={handleApprove}
                                                    >
                                                        {isSubmitting ? "Approving…" : "Approve Request"}
                                                    </Button>
                                                );
                                            }

                                        })()}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div >
            }
        >
            <div className={`space-y-3 sm:space-y-5 text-slate-700 text-sm transition-all duration-300 ${rejectionMode || showConfirmActivation || showConfirmDeactivation || showAssignmentSection ? 'pb-24 sm:pb-1' : 'pb-1'}`}>

                <div className="grid grid-cols-2 gap-3 sm:gap-5">
                    <div className="space-y-1 sm:space-y-1.5">
                        <label className="text-xs font-semibold text-slate-600/90">Full Name</label>
                        <div className="w-full px-3 py-1.5 sm:py-2 bg-white rounded-xl font-medium text-xs text-slate-600 border border-slate-100/70">
                            {singleRequst?.requestername || 'Unknown Requester'}
                        </div>
                    </div>

                    <div className="space-y-1 sm:space-y-1.5">
                        <label className="text-xs font-normal text-slate-600/90">Request Type</label>
                        <div className="w-full px-3 py-1.5 sm:py-2 bg-white rounded-xl font-medium text-xs text-slate-600 border border-slate-100/70">
                            {singleRequst?.requesttype === "deactivation" ? "Deactivation" : "New Request"}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3 sm:gap-5">
                    <div className="space-y-1 sm:space-y-1.5">
                        <label className="text-xs font-normal text-slate-600/90">Oracle Identification</label>
                        <div className="w-full px-3 py-1.5 sm:py-2 bg-white rounded-xl font-medium text-xs text-slate-600 border border-slate-100/70">
                            {singleRequst?.requesterId || 'N/A'}
                        </div>
                    </div>

                    <div className="space-y-1 sm:space-y-1.5">
                        <label className="text-xs font-normal text-slate-600/90">Allocated Site Access</label>
                        <div className="w-full px-3 py-1.5 sm:py-2 bg-white rounded-xl font-medium text-xs text-slate-600 border border-slate-100/70 truncate">
                            {singleRequst?.allowedsite?.map((s: any) => s.sitename).join(", ") || 'No assigned scopes'}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3 sm:gap-5">
                    <div className="space-y-1 sm:space-y-1.5">
                        <label className="text-xs font-normal text-slate-600/90">Start Date</label>
                        <div className="w-full px-3 py-1.5 sm:py-2 bg-white rounded-xl font-medium text-xs text-slate-600 border border-slate-100/70">
                            {singleRequst?.startdate ? moment(singleRequst.startdate).format('MMM D, YYYY') : 'N/A'}
                        </div>
                    </div>

                    <div className="space-y-1 sm:space-y-1.5">
                        <label className="text-xs font-normal text-slate-600/90">Expiration Date</label>
                        <div className="w-full px-3 py-1.5 sm:py-2 bg-white rounded-xl font-medium text-xs text-slate-600 border border-slate-100/70">
                            {singleRequst?.enddate ? moment(singleRequst.enddate).format('MMM D, YYYY') : 'N/A'}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3 sm:gap-5">
                    <div className="space-y-1 sm:space-y-1.5">
                        <label className="text-xs font-normal text-slate-600/90">Current Status</label>
                        <div className="w-full px-3 py-1.5 sm:py-2 bg-white rounded-xl font-medium text-xs text-slate-600 border border-slate-100/70 flex items-center gap-2">
                            {(() => {
                                const status = singleRequst?.status;
                                if (status === "Approved" || status === "Activated") {
                                    const statusText = singleRequst?.requesttype === "deactivation" ? "Deactivated" : "Activated";
                                    return (
                                        <>
                                            <CheckCircle2 className="w-3.5 h-3.5 text-[#8dc63f] shrink-0" />
                                            <span className="text-slate-700">{statusText}</span>
                                        </>
                                    );
                                }
                                if (status === "Rejected") {
                                    return (
                                        <>
                                            <XCircle className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                                            <span className="text-slate-700">{status}</span>
                                        </>
                                    );
                                }
                                return (
                                    <>
                                        <Clock3 className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                                        <span className="text-slate-700">{status || "Pending"}</span>
                                    </>
                                );
                            })()}
                        </div>
                    </div>

                    <div className="space-y-1 sm:space-y-1.5">
                        <label className="text-xs font-normal text-slate-600/90">Current Reviewer</label>
                        <div className="w-full px-3 py-1.5 sm:py-2 bg-white rounded-xl font-medium text-xs text-slate-600 border border-slate-100/70 truncate">
                            {singleRequst?.approver?.fullName || 'N/A'} <span className="text-slate-400/80 text-xs font-normal">({singleRequst?.approvalStage || 'N/A'})</span>
                        </div>
                    </div>
                </div>

                {singleRequst?.reciept_attachment && (
                    <div className="space-y-1 sm:space-y-1.5">
                        <label className="text-xs font-normal text-slate-600/90">Receipt Attachment</label>
                        <a
                            href={
                                window.location.hostname === "localhost"
                                    ? `http://localhost:7271/uploads/${singleRequst.reciept_attachment.replace(/^.*[\\/]/, '')}`
                                    : `/uploads/${singleRequst.reciept_attachment.replace(/^.*[\\/]/, '')}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 w-full px-3 py-1.5 sm:py-2 bg-white rounded-xl text-xs text-[#8dc63f] font-medium border border-slate-100/70 hover:border-[#8dc63f]/40 hover:bg-[#8dc63f]/5 transition-all group"
                        >
                            <Paperclip className="w-3.5 h-3.5 shrink-0" />
                            <span className="truncate flex-1">{singleRequst.reciept_attachment.replace(/^.*[\\/]/, '')}</span>
                            <ExternalLink className="w-3 h-3 opacity-50 group-hover:opacity-100 shrink-0" />
                        </a>
                    </div>
                )}

                <div
                    className={`transition-all duration-300 ease-in-out overflow-hidden ${rejectionMode ? "max-h-[300px] opacity-100 pt-1 pb-0" : "max-h-0 opacity-0 !m-0 !p-0"}`}
                >
                    <div className="space-y-2 sm:space-y-1.5">
                        <label className="text-xs font-normal text-slate-600/90">Provide Rejection Reason</label>
                        <textarea
                            ref={rejectionRef}
                            className="w-full p-2 sm:p-3 bg-white border border-slate-200 rounded-xl text-xs sm:text-xs focus:ring-2 focus:ring-rose-500/10 focus:border-rose-300 transition-all outline-none min-h-[60px] sm:min-h-[80px] text-slate-600"
                            placeholder="Specify reason for rejection"
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                        />
                    </div>
                </div>

                <div
                    className={`transition-all duration-300 ease-in-out overflow-hidden ${showAssignmentSection && (singleRequst?.approvalStage === "Electronic Security Manager" || singleRequst?.approvalStage === "Security and Surveillance Supervisor")
                        ? "max-h-[500px] opacity-100 pt-1 pb-3"
                        : "max-h-0 opacity-0 !m-0 !p-0"
                        }`}
                >
                    <div ref={assignmentRef} className="space-y-3 p-0.5">
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-normal text-slate-600/90">
                                {singleRequst?.approvalStage === "Electronic Security Manager" ? "Select Authorized Supervisor" : "Select Card Provisioning Activator"}
                            </span>
                        </div>
                        <div className="grid grid-cols-1 gap-1.5 bg-slate-50/50 border border-slate-100 p-2 rounded-2xl max-h-72 overflow-y-auto">
                            {filteredEmp?.length === 0 ? (
                                <p className="text-xs text-slate-400 text-center py-4">No authorized staff members found in this category.</p>
                            ) : (
                                filteredEmp?.map((staff: any, index: number) => {
                                    const staffId = singleRequst?.approvalStage === "Security and Surveillance Supervisor"
                                        ? (staff.employeeId || staff.id)
                                        : (staff.id || staff.oracleId || staff._id);
                                    return (
                                        <label key={staff._id || index} className="flex items-center gap-2.5 p-2 rounded-xl border border-slate-100 bg-white hover:border-[#8dc63f]/50 hover:bg-slate-100/40 transition-all cursor-pointer group min-h-[38px]">
                                            <input
                                                type="radio"
                                                id={`staff-${staffId}`}
                                                name="staff-select"
                                                className="w-3.5 h-3.5 shrink-0 border border-slate-300 appearance-none rounded-full checked:border-[#8dc63f] checked:bg-[radial-gradient(circle,_#8dc63f_40%,_transparent_50%)] transition-all cursor-pointer"
                                                checked={selectedStaffs[0] === staffId}
                                                onChange={() => setSelectedStaffs([staffId])}
                                            />

                                            <div className="flex flex-col min-w-0 flex-1">
                                                <span className="text-xs font-semibold text-slate-600 group-hover:text-slate-800 truncate">{staff.fullName}</span>
                                                <span className="text-[10px] text-slate-400/90 font-medium tracking-tight truncate">{staff.role?.name || 'Authorized Staff'}</span>
                                            </div>
                                        </label>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                {(() => {
                    const stage = singleRequst?.approvalStage;
                    const reqType = singleRequst?.requesttype;

                    if ((reqType === "new" || reqType === "replacement") && stage === "Card Activator") {
                        return (
                            <div
                                className={`transition-all duration-300 ease-in-out overflow-hidden ${!rejectionMode && showConfirmActivation ? "max-h-[300px] opacity-100" : "max-h-0 opacity-0 !m-0 !p-0"}`}
                            >
                                <div className="mx-4 p-4 bg-gray-50 border border-gray-200 rounded-2xl space-y-3">
                                    <div className="flex items-center gap-2">
                                        <label className="text-xs font-semibold text-slate-700">Enter Card ID</label>
                                    </div>
                                    <input
                                        ref={cardInputRef}
                                        type="text"
                                        autoComplete="off"
                                        autoCorrect="off"
                                        autoCapitalize="characters"
                                        placeholder="Scan or enter card hex code..."
                                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-xs focus:ring-1 focus:ring-[#8dc63f]/70 transition-all outline-none text-slate-600"
                                        value={physicalCardId}
                                        onChange={(e) => setPhysicalCardId(e.target.value)}
                                    />
                                </div>
                            </div>
                        );
                    }

                    if (reqType === "maintenance") {
                        if (stage === "Card Activator") {
                            return (
                                <div
                                    className={`transition-all duration-300 ease-in-out overflow-hidden ${!rejectionMode && showConfirmActivation ? "max-h-[300px] opacity-100 py-0" : "max-h-0 opacity-0 !m-0 !p-0"}`}
                                >
                                    <div ref={deactivationRef} className="mx-4 p-2 mb-4 bg-[#8dc63f]/5 border border-[#8dc63f]/20 rounded-xl text-center">
                                        <p className="text-xs text-[#8dc63f] font-medium">
                                            Confirming will update the requested maintenance door access permissions for this card.
                                        </p>
                                    </div>
                                </div>
                            );
                        }
                    }

                    if (reqType === "deactivation" && stage === "Card Activator") {
                        return (
                            <div
                                className={`transition-all duration-300 ease-in-out overflow-hidden ${!rejectionMode && showConfirmDeactivation ? "max-h-[300px] opacity-100 py-4" : "max-h-0 opacity-0 !m-0 !p-0"}`}
                            >
                                <div ref={deactivationRef} className="mx-4 p-2 mb-4 bg-red-50 border border-red-200 rounded-xl text-center transition-all duration-300">
                                    <p className="text-xs text-red-700 transition-opacity duration-300">
                                        Confirming will instantly deactivate this card and revoke all door access.
                                    </p>
                                </div>
                            </div>
                        );
                    }
                    return null;
                })()}

                <div ref={footerRef} className="h-0 !m-0 !p-0" />
            </div>
        </GlassModal >
    );
};

export default connect(
    (state: any) => ({
        user: state.auth.user,
        singleRequst: state.request.singleRequst,
    }),
    {
        approveRequest,
        rejectRequest,
        setRequest,
        getAssigneeEmployee,
        assignSupervisor,
        getActivators,
        assignStaffs
    }
)(RequestDetails);